/**
 * Top-level Build-page container, mounted only at `/build/:slug` (the
 * hero form on `/` collects link + email and navigates here). Resolves
 * the build state off the URL slug + API:
 *
 *   - status queued/building → `Pipeline` (live, animated, SSE-driven)
 *   - status rejected → `Rejected`
 *   - status ready    → `Review` (the KB admin)
 *   - status error    → falls into the same Rejected-style page with
 *                       the error string; UX-wise these are the same
 *                       "this didn't work" outcome.
 *
 * Data flow:
 *   1. On mount with a slug, fetch `/kb/{slug}` once.
 *   2. If the build is still queued/building, open an `EventSource` to
 *      `/build/{id}/stream` and update local state on each `status` event.
 *   3. When status transitions to ready/rejected/error, close the
 *      stream and refetch `/kb/{slug}` so we pick up the populated
 *      `profile` payload that arrives when the pipeline upserts it.
 */

import { useCallback, useEffect, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";

import { API_URL, getKb } from "./api.js";
import { Icon } from "./Icon.js";
import { Pipeline } from "./Pipeline.js";
import { Rejected } from "./Rejected.js";
import { Review } from "./Review.js";
import type { Build, BusinessProfile } from "./types.js";

type LoadState =
  | { kind: "loading" }
  | { kind: "missing" }
  | { kind: "loaded"; build: Build; profile: BusinessProfile | null }
  | { kind: "error"; message: string };

const TERMINAL: ReadonlySet<Build["status"]> = new Set([
  "ready",
  "rejected",
  "error",
]);

export function BuildPage() {
  const { slug } = useParams<{ slug?: string }>();

  // The route is `/build/:slug`, so a missing slug should not happen in
  // practice. Guard anyway: send the operator back to the hero form on
  // `/` rather than rendering an empty shell.
  if (!slug) {
    return <Navigate to="/" replace />;
  }

  return <SlugView slug={slug} />;
}

/**
 * Renders the right phase based on the loaded build. Lives below the
 * router-param branch so the data hooks only run when there's a slug.
 */
function SlugView({ slug }: { slug: string }) {
  const [state, setState] = useState<LoadState>({ kind: "loading" });

  const refresh = useCallback(async () => {
    try {
      const res = await getKb(slug);
      if (!res) {
        setState({ kind: "missing" });
        return;
      }
      setState({ kind: "loaded", build: res.build, profile: res.profile });
      return res;
    } catch (err) {
      setState({
        kind: "error",
        message: err instanceof Error ? err.message : "load failed",
      });
      return null;
    }
  }, [slug]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // SSE subscription — only while the build is still in flight. Closes
  // and refetches once we hit a terminal status so the populated
  // BusinessProfile shows up on the Review screen.
  useEffect(() => {
    if (state.kind !== "loaded") return;
    if (TERMINAL.has(state.build.status)) return;

    const buildId = state.build.id;
    const url = `${API_URL}/build/${encodeURIComponent(buildId)}/stream`;
    const es = new EventSource(url);

    es.addEventListener("status", (ev) => {
      try {
        const updated = JSON.parse((ev as MessageEvent).data) as Build;
        setState((prev) => {
          if (prev.kind !== "loaded") return prev;
          return { ...prev, build: updated };
        });
        if (TERMINAL.has(updated.status)) {
          es.close();
          void refresh();
        }
      } catch {
        /* malformed event — ignore, the next refresh will reconcile */
      }
    });

    es.addEventListener("error", () => {
      // EventSource will auto-reconnect on transient failures; let it.
      // If the server returned a terminal error, the stream just ends —
      // a one-shot refresh covers both cases.
      void refresh();
    });

    return () => {
      es.close();
    };
  }, [state, refresh]);

  if (state.kind === "loading") {
    return (
      <BuildShell stepIndex={1}>
        <LoadingSkeleton />
      </BuildShell>
    );
  }

  if (state.kind === "missing") {
    return (
      <BuildShell stepIndex={0}>
        <MissingSlug slug={slug} />
      </BuildShell>
    );
  }

  if (state.kind === "error") {
    return (
      <BuildShell stepIndex={1}>
        <ErrorState message={state.message} />
      </BuildShell>
    );
  }

  const { build, profile } = state;

  if (build.status === "rejected") {
    return (
      <BuildShell stepIndex={2}>
        <Rejected build={build} />
      </BuildShell>
    );
  }

  if (build.status === "error") {
    return (
      <BuildShell stepIndex={2}>
        <Rejected build={build} />
      </BuildShell>
    );
  }

  if (build.status === "ready" && profile) {
    return (
      <BuildShell stepIndex={2} wide>
        <Review
          build={build}
          profile={profile}
          onProfileUpdate={(next) => {
            setState((prev) =>
              prev.kind === "loaded" ? { ...prev, profile: next } : prev,
            );
          }}
        />
      </BuildShell>
    );
  }

  // status ∈ {queued, building}, or ready-but-profile-not-yet-written
  // (Review needs profile; until it lands, keep the Pipeline on screen).
  return (
    <BuildShell stepIndex={1}>
      <Pipeline
        businessName={profile?.name ?? friendlyName(build.source)}
        slug={build.slug ?? undefined}
      />
    </BuildShell>
  );
}

/**
 * Best-effort business-name derivation from the build source — same idea
 * as the API's `_derive_business_name` so the Pipeline header reads
 * sensibly while we wait for the real extracted name.
 */
function friendlyName(source: string): string {
  const s = (source || "").trim();
  if (!s) return "Your business";
  const noProto = s.replace(/^https?:\/\//i, "").replace(/^www\./i, "");
  const host = noProto.split("/")[0]?.split("?")[0]?.split("#")[0] ?? "";
  if (!host || host.includes(" ") || !host.includes(".")) {
    return s.length > 40 ? "Your business" : s;
  }
  const parts = host.split(".");
  const suffixes = new Set([
    "com",
    "au",
    "net",
    "org",
    "co",
    "nz",
    "io",
    "dev",
  ]);
  while (parts.length > 1) {
    const last = parts[parts.length - 1]?.toLowerCase();
    if (!last || !suffixes.has(last)) break;
    parts.pop();
  }
  const stem = parts.join(".");
  const cleaned = stem.replace(/[^a-zA-Z0-9]+/g, " ").trim();
  if (!cleaned) return "Your business";
  return cleaned
    .split(" ")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

/** Sticky top bar + main column wrapper, identical to the prototype. */
function BuildShell({
  children,
  stepIndex,
  wide,
}: {
  children: React.ReactNode;
  stepIndex: number;
  wide?: boolean;
}) {
  return (
    <div className="build-page">
      <header className="build-top">
        <Link to="/" className="logo">
          <img src="/img/offsider-monogram.svg" alt="" />
          Offsider
        </Link>
        <div className="bf-dots" aria-hidden="true">
          {["enter", "building", "review"].map((p, i) => (
            <i
              key={p}
              className={i === stepIndex ? "on" : i < stepIndex ? "past" : ""}
            />
          ))}
        </div>
        <Link to="/" className="build-back">
          <Icon name="arrow-left" size={16} /> Back to site
        </Link>
      </header>

      <main className={`build-main${wide ? " hub-main-wrap" : ""}`}>
        {children}
      </main>
    </div>
  );
}

function LoadingSkeleton() {
  return (
    <div
      className="fade-up"
      style={{
        textAlign: "center",
        padding: "60px 20px",
        color: "var(--ink-3)",
      }}
    >
      <Icon name="loader" size={32} className="dial" color="var(--accent)" />
      <p style={{ marginTop: 16, fontSize: 15 }}>Loading your build…</p>
    </div>
  );
}

function MissingSlug({ slug }: { slug: string }) {
  return (
    <div
      className="fade-up"
      style={{ maxWidth: 480, margin: "60px auto", textAlign: "center" }}
    >
      <h1
        style={{
          fontFamily: "var(--font-display)",
          fontSize: 28,
          fontWeight: 800,
        }}
      >
        We can't find that build.
      </h1>
      <p style={{ color: "var(--ink-2)", margin: "12px auto 24px" }}>
        The slug <code style={{ fontFamily: "var(--font-mono)" }}>{slug}</code>{" "}
        doesn't match anything in our system. Builds expire after 30 days if the
        email on file never returns — yours may have been one of those.
      </p>
      <Link to="/" className="btn btn-primary">
        Start a new build
      </Link>
    </div>
  );
}

function ErrorState({ message }: { message: string }) {
  return (
    <div
      className="fade-up"
      style={{ maxWidth: 480, margin: "60px auto", textAlign: "center" }}
    >
      <h1
        style={{
          fontFamily: "var(--font-display)",
          fontSize: 28,
          fontWeight: 800,
        }}
      >
        Something went wrong loading this build.
      </h1>
      <p style={{ color: "var(--ink-2)", margin: "12px auto 24px" }}>
        {message}
      </p>
      <Link to="/" className="btn btn-secondary">
        Try again
      </Link>
    </div>
  );
}
